import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { ReactNode, useState } from "react";
import SettingsIcon from "@mui/icons-material/Settings";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import shiColors from "../../theme/shiColors";
import {
  formatActivityTime,
  activityEntryDescription,
} from "./activityHelpers";
import ActivityChangeDescription from "./ActivityChangeDescription";
import RevertConfirmationDialog from "./RevertConfirmationDialog";
import type { EnrichedActivity } from "./FilteredActivityList";

export interface ActivityChange {
  key?: string;
  oldValue?: any;
  newValue?: any;
}

export interface Activity {
  id?: string;
  changedAt?: Date;
  change?: ActivityChange;
  changedBy?: ActivityChangedUser;
}

export interface ActivityChangedUser {
  entraId?: string;
  fullName?: string;
  emailAddress?: string;
}

interface UserInfoProps {
  changedBy: ActivityChangedUser | undefined;
  avatarElement?: ReactNode;
}

const UserInfo = ({ changedBy, avatarElement }: UserInfoProps) => {
  if (!changedBy) return null;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, ml: 2 }}>
      {avatarElement}
      <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
        {changedBy.fullName}
      </Typography>
    </Box>
  );
};

export interface ActivityEntryProps {
  activity: EnrichedActivity;
  handleRevert: (
    key: string,
    oldValue: any,
    uniqueId: string | undefined,
  ) => Promise<void> | void;
  isRevertPending?: boolean;
}

export default function ActivityEntry({
  activity,
  handleRevert,
  isRevertPending = false,
}: Readonly<ActivityEntryProps>) {
  const [confirmRevertOpen, setConfirmRevertOpen] = useState(false);
  const [revertError, setRevertError] = useState<string | null>(null);

  const Icon = activity.eventIcon || SettingsIcon;
  const color = activity.eventColor || shiColors.gray.light;
  const activityAny = activity as any;
  const friendlyName = activityAny.friendlyPropertyName;
  const displayName = friendlyName || activity.change?.key || "Update";
  const activityTypeLabel =
    friendlyName ||
    (activity.change?.key
      ? activity.change.key.charAt(0).toUpperCase() +
        activity.change.key.slice(1).toLowerCase()
      : "Update");
  const showRevert = activity.showRevert !== false;

  const handleRevertClick = () => {
    setConfirmRevertOpen(true);
    setRevertError(null);
  };

  const handleConfirmRevert = async () => {
    try {
      setRevertError(null);
      await handleRevert(
        activity.change.key,
        activity.change.oldValue,
        activity.id,
      );
      setConfirmRevertOpen(false);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to revert change. Please try again.";
      setRevertError(errorMessage);
    }
  };

  const handleCancelRevert = () => {
    setConfirmRevertOpen(false);
    setRevertError(null);
  };

  const canRevert =
    showRevert &&
    activity.change?.key &&
    activity.change.oldValue !== undefined &&
    !isRevertPending;

  return (
    <>
      <Box
        key={activity.id}
        role="article"
        aria-label={`Activity: ${activityTypeLabel} changed by ${activity.changedBy?.fullName || "unknown"}`}
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "flex-start",
          p: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.25,
            minWidth: 50,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              borderRadius: "50%",
              backgroundColor: color,
              marginTop: "3px",
            }}
            aria-hidden="true"
          >
            <Icon sx={{ fontSize: "1.5rem", color: "primary.contrastText" }} />
          </Box>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary" }}
            aria-label={`Changed at ${activity.changedAt ? formatActivityTime(activity.changedAt) : "unknown time"}`}
          >
            {activity.changedAt
              ? formatActivityTime(activity.changedAt)
              : "unknown time"}
          </Typography>
        </Box>
        <Paper
          sx={{
            px: 2,
            py: 1,
            backgroundColor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            flex: 1,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            {activity.change && (
              <Box>
                <Typography variant="body2">{displayName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  <ActivityChangeDescription
                    {...activityEntryDescription(
                      activity.change.oldValue,
                      activity.change.newValue,
                    )}
                  />
                </Typography>
              </Box>
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {showRevert && (
                <Tooltip
                  title={
                    canRevert
                      ? "Revert to previous state"
                      : "Cannot revert this change"
                  }
                  arrow
                >
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Revert changes"
                      onClick={handleRevertClick}
                      disabled={!canRevert}
                    >
                      {isRevertPending ? (
                        <CircularProgress size={20} />
                      ) : (
                        <SettingsBackupRestoreIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <UserInfo
                changedBy={activity.changedBy}
                avatarElement={activity.avatarElement}
              />
            </Box>
          </Box>
        </Paper>
      </Box>
      {/* Revert Confirmation Dialog */}
      <RevertConfirmationDialog
        open={confirmRevertOpen}
        onCancel={handleCancelRevert}
        onConfirm={handleConfirmRevert}
        isPending={isRevertPending}
        error={revertError}
        fieldLabel={activity.change?.key}
        previousValues={[{ value: activity.change?.oldValue }]}
      />
    </>
  );
}
